import {Button, Form, Input, Modal, Popconfirm, Select, Table} from 'antd';
import {DeleteOutlined, UserAddOutlined} from '@ant-design/icons';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../../../common/hooks';
import {
  searchBy,
  pageNumberChange,
  pageSizeChange,
  stationGroupEmployeesList,
  updateStationGroupEmployees,
  sortBy,
} from './stationGroupEmployeesSlice';
import type {STATION_GROUP_EMPLOYEES} from './stationGroupEmployees.type';
import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {employeesList, clearEmployeesFilters} from '../../../employee/employees/employeeSlice';
import {debounce} from 'lodash';
import {debounceDelay, sortbyFilter} from '../../../../../common/constants';

const Main = ({groupId}: {groupId: number}) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.stationGroupEmployees);
  const userPermission = useAppSelector((state) => state.config.config.permissions);
  const [addEmployeeModalOpen, setAddEmployeeModalOpen] = useState(false);
  const employeesOption = useAppSelector((state) => state.employees.employees.pageData);
  const [addEmpForm] = Form.useForm();
  const {Option} = Select;

  const handleFilterSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    }
  };

  const handleEmployeeBouncing = debounce((e) => {
    dispatch(employeesList({search: e, is_active: true}));
  }, debounceDelay);

  const handleAddEmployeeModalClose = () => {
    addEmpForm.resetFields();
    setAddEmployeeModalOpen(false);
    dispatch(clearEmployeesFilters());
  };

  const handleAddEmp = ({employee_ids}: {employee_ids: number[]}) => {
    employee_ids = employee_ids.map(Number);
    const temp = state.groupEmployeesList.pageData.map((x) => x.id);
    employee_ids = [...employee_ids, ...temp];

    dispatch(updateStationGroupEmployees({groupId: groupId, employee_ids: employee_ids}))
      .unwrap()
      .then(() => {
        handleAddEmployeeModalClose();
        dispatch(stationGroupEmployeesList({groupId: groupId}));
      });
  };

  const handleRemoveEmp = (empId: number) => {
    const emps = state.groupEmployeesList.pageData.map((x) => x.id);
    const payload = emps.filter((x) => x != empId);
    dispatch(updateStationGroupEmployees({groupId: groupId, employee_ids: payload}))
      .unwrap()
      .then(() => {
        dispatch(stationGroupEmployeesList({groupId: groupId}));
      });
  };
  const columns: ColumnsType<STATION_GROUP_EMPLOYEES> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return (
          <>
            {(state.groupEmployeesList.page_number - 1) * state.groupEmployeesList.page_size +
              index +
              1}
          </>
        );
      },
    },
    {
      title: 'Full Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'User Name',
      dataIndex: 'username',
      sorter: true,
    },
    {title: 'Email', dataIndex: 'email', sorter: true},
    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            {userPermission.update_stationgroup && (
              <Popconfirm
                title='Are you sure '
                onConfirm={() => {
                  handleRemoveEmp(record.id);
                }}
                okText='Yes'
                cancelText='No'
              >
                <DeleteOutlined
                  style={{cursor: 'pointer', fontSize: '16px', color: 'red', marginLeft: '10px'}}
                />
              </Popconfirm>
            )}
          </>
        );
      },
    },
  ];
  !userPermission.update_stationgroup ? (columns.length = 3) : null;

  useEffect(() => {
    dispatch(
      stationGroupEmployeesList({
        groupId: groupId,
        filters: {
          page_size: state.groupEmployeesList.page_size,
          page_number: state.groupEmployeesList.page_number,
          sort_by: state.groupEmployeesList.sort_by,
          ascending: state.groupEmployeesList.ascending,
          search: state.groupEmployeesList.search,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.groupEmployeesList.page_number,
    state.groupEmployeesList.page_size,
    state.groupEmployeesList.search,
    state.groupEmployeesList.ascending,
    state.groupEmployeesList.sort_by,
  ]);
  return (
    <>
      <div>
        <div
          style={{
            margin: '10px 0px 10px 0px',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: '350px 1fr',
          }}
        >
          <Input.Search
            placeholder='Search'
            defaultValue={state.groupEmployeesList.search}
            style={{borderRadius: '10px'}}
            allowClear
            onSearch={(e) => handleFilterSearch(e)}
          />
          {userPermission.update_stationgroup && (
            <div style={{width: '100%', textAlign: 'end'}}>
              <Button
                type='primary'
                style={{borderRadius: '5px', marginInline: '5px'}}
                icon={<UserAddOutlined />}
                onClick={() => {
                  setAddEmployeeModalOpen(true);
                  dispatch(employeesList({is_active: true}));
                }}
              >
                Add Employee
              </Button>
            </div>
          )}
        </div>
        <Table
          rowKey='id'
          columns={columns}
          pagination={
            {
              total: state.groupEmployeesList.totalDataCount,

              current: state.groupEmployeesList.page_number,
              pageSize: state.groupEmployeesList.page_size,
              showSizeChanger: true,
              // onChange: (pageNumber, pageSize: number) => {
              //   if (pageSize !== state.groupEmployeesList.page_size) {
              //     dispatch(pageSizeChange({pageNumber, pageSize}));
              //   }
              //   dispatch(pageNumberChange(pageNumber));
              // },
            } as TablePaginationConfig
          }
          loading={state.isLoading}
          size='middle'
          dataSource={state.groupEmployeesList.pageData}
          scroll={{x: undefined, y: '60vh'}}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange={(pagination, _, {order, field}: any) => {
            if (pagination.pageSize && pagination.pageSize !== state.groupEmployeesList.page_size) {
              dispatch(pageSizeChange(pagination.pageSize));
            }
            if (pagination.current && pagination.current !== state.groupEmployeesList.page_number) {
              dispatch(pageNumberChange(pagination.current));
            }
            if (field) {
              if (order === sortbyFilter.DESCEND) {
                dispatch(sortBy({ascending: false, sortBy: field}));
              } else {
                dispatch(sortBy({ascending: true, sortBy: field}));
              }
            }
          }}
        />
      </div>
      <Modal
        title='Add Employee In Station Group'
        open={addEmployeeModalOpen}
        onCancel={() => {
          handleAddEmployeeModalClose();
        }}
        okText={'Add'}
        maskClosable={false}
        style={{height: '700px', width: '800px'}}
        onOk={() => {
          addEmpForm
            .validateFields()
            .then((values) => {
              handleAddEmp(values);
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Form form={addEmpForm} name='Add Station' layout='vertical'>
          <Form.Item
            label='Employees'
            name='employee_ids'
            rules={[{required: true, message: 'At least one Employee is required'}]}
          >
            <Select
              showSearch
              mode='multiple'
              placeholder='Search Station'
              defaultActiveFirstOption={false}
              showArrow={false}
              filterOption={false}
              onSearch={(e) => {
                if (e.length > 3) handleEmployeeBouncing(e);
              }}
              notFoundContent={null}
              //loading={}
            >
              {employeesOption.map((x) => (
                <Option key={x.id}>{x.username}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Main;
